import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { getOrderSession, sipCancelOrder } from "../../../api/bseApi";

/**
 * SipCancelScreen - BSE StAR MF SIP Cancellation
 * Opens from SIP List / SIP Details screen
 * Receives: ClientCode, SIP REGID, SchemeCd from location.state
 */
export default function SipCancelScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const [context, setContext] = useState(null);
  const [form, setForm] = useState({
    cancelReason: "",
    confirmed: false,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const state = location.state;
    const params = new URLSearchParams(location.search);
    const clientCode = state?.clientCode || params.get("clientCode");
    const regId = state?.regId || params.get("regId");
    const schemeCd = state?.schemeCd || params.get("schemeCd");
    if (clientCode && regId && schemeCd) {
      setContext({
        clientCode,
        regId,
        schemeCd,
        schemeName: state?.schemeName || params.get("schemeName") || "",
      });
      return;
    }
    Swal.fire("Error", "Missing SIP details. Open from SIP List or SIP Details with ClientCode, REGID, SchemeCd.", "error").then(() => navigate("/user"));
  }, [location.state, location.search, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const err = {};
    if (!form.confirmed) err.confirmed = "You must confirm cancellation to proceed";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!context || !validate()) return;

    setLoading(true);
    try {
      const sessionRes = await getOrderSession();
      if (sessionRes.data?.status !== 1) {
        const msg = sessionRes.data?.error || sessionRes.data?.message || "Session failed";
        Swal.fire("Error", msg, "error");
        setLoading(false);
        return;
      }

      const { encryptedPassword, passKey } = sessionRes.data.data;

      const payload = {
        encryptedPassword,
        passKey,
        clientCode: context.clientCode,
        schemeCd: context.schemeCd,
        regId: context.regId,
      };

      const res = await sipCancelOrder(payload);

      if (res.data?.status === 1) {
        Swal.fire({
          icon: "success",
          title: "SIP Cancelled",
          text: "SIP cancelled successfully.",
        });
        navigate("/user");
      } else {
        const msg = res.data?.bseMessage || res.data?.message || res.data?.error || "SIP cancellation failed";
        Swal.fire("Error", msg, "error");
      }
    } catch (err) {
      const msg =
        err.response?.data?.bseMessage ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "SIP cancellation failed";
      Swal.fire("Error", msg, "error");
    } finally {
      setLoading(false);
    }
  };

  if (!context) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "20px",
            }}
          >
            <nav aria-label="breadcrumb" style={{ marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link> </li>
                <li className="breadcrumb-item"><Link to="/user">Users</Link> </li>
                <li className="breadcrumb-item active">SIP Cancel</li>
              </ol>
            </nav>
            <h5>SIP Cancellation</h5>
            <p className="text-muted mb-0">
              Client Code: {context.clientCode}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div
              className="mb-4"
              style={{
                background: "#ffffff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                padding: "24px",
                border: "1px solid #e6eef0",
              }}
            >
              <h6 className="mb-4" style={{ color: "#0b6f68", fontWeight: "600" }}>
                SIP Cancellation
              </h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">SIP Registration ID (REGID)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={context.regId}
                    readOnly
                    style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Scheme (SchemeCd)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={context.schemeName ? `${context.schemeName} (${context.schemeCd})` : context.schemeCd}
                    readOnly
                    style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Cancel Reason (optional)</label>
                  <textarea
                    className="form-control"
                    name="cancelReason"
                    value={form.cancelReason}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Enter reason for cancellation (optional)"
                  />
                </div>
                <div className="col-12">
                  <div className={`form-check ${errors.confirmed ? "is-invalid" : ""}`}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="confirmCancel"
                      name="confirmed"
                      checked={form.confirmed}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="confirmCancel">
                      I confirm that I want to cancel this SIP registration. This action cannot be undone.
                    </label>
                  </div>
                  {errors.confirmed && <div className="invalid-feedback d-block">{errors.confirmed}</div>}
                </div>
              </div>

              <div className="d-flex gap-2 justify-content-end mt-4 pt-3" style={{ borderTop: "1px solid #e6eef0" }}>
                <Link to="/user" className="btn btn-secondary">
                  Cancel
                </Link>
                <button type="submit" className="btn btn-danger" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      Cancelling...
                    </>
                  ) : (
                    "Cancel SIP"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
